/**
 * Hooks for item ratings: batch load for the results review + upsert/delete.
 *
 * @module useItemRatings
 */

import { useQuery } from '@tanstack/react-query';
import {
  getRatingsForTargets,
  type RatingDetail,
} from '@/lib/ratingsApi';

export const ITEM_RATINGS_QUERY_KEY = ['itemRatings'] as const;

/**
 * Batch-fetch the current user's ratings for the given target ids and return a
 * lookup keyed by `${targetType}:${targetId}`.
 */
export function useItemRatings(targetIds: string[], enabled: boolean = true) {
  const sortedKey = [...targetIds].sort();
  const { data, isLoading, isError, refetch } = useQuery<RatingDetail[]>({
    queryKey: [...ITEM_RATINGS_QUERY_KEY, sortedKey],
    queryFn: () => getRatingsForTargets(targetIds),
    enabled: enabled && targetIds.length > 0,
    staleTime: 0,
  });

  const byKey = new Map<string, RatingDetail>();
  (data ?? []).forEach((rating) => {
    byKey.set(`${rating.targetType}:${rating.targetId}`, rating);
  });

  return { ratings: data ?? [], byKey, isLoading, isError, refetch };
}
